import React, { useState, useCallback, useMemo } from 'react';
import { Dropdown, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MARKER_GROUPS, EDITABLE_MARKERS, R_PEAK_TYPE, getMarkerByType } from './MarkerEditingManager';
import './MarkerEditingToolbar.css';

/**
 * Contextual toolbar for ECG marker editing.
 * Appears when edit mode is enabled, providing marker type selection
 * and clear instructions on how to add/remove markers.
 *
 * Marker definitions are imported from MarkerEditingManager.js (single source of truth).
 */
const MarkerEditingToolbar = ({
  selectedMarkerType = R_PEAK_TYPE,
  onMarkerTypeChange,
  onSave,
  onCancel,
  hasPendingChanges = false
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedMarker = useMemo(() =>
    getMarkerByType(selectedMarkerType) || EDITABLE_MARKERS[0],
    [selectedMarkerType]
  );

  const handleMarkerSelect = useCallback((marker) => {
    onMarkerTypeChange?.(marker.type);
    setIsDropdownOpen(false);
  }, [onMarkerTypeChange]);

  const renderMarkerButton = (marker, isSelected = false) => (
    <div
      className={`marker-option ${isSelected ? 'selected' : ''}`}
      style={{ '--marker-color': marker.color }}
    >
      <span className="marker-indicator" />
      <span className="marker-label">{marker.label}</span>
    </div>
  );

  return (
    <div className="marker-editing-toolbar">
      <div className="toolbar-header">
        <div className="toolbar-title">
          <span className="pulse-dot" />
          <span>{t('markerEditing.title')}</span>
        </div>
      </div>

      <div className="toolbar-content">
        <div className="marker-selector-section">
          <span className="section-label">{t('markerEditing.selectedMarker')}:</span>

          <Dropdown
            show={isDropdownOpen}
            onToggle={setIsDropdownOpen}
            className="marker-dropdown"
          >
            <Dropdown.Toggle
              variant="light"
              className="marker-dropdown-toggle"
              style={{ '--marker-color': selectedMarker.color }}
            >
              {renderMarkerButton(selectedMarker, true)}
              <span className="dropdown-chevron">▼</span>
            </Dropdown.Toggle>

            <Dropdown.Menu className="marker-dropdown-menu">
              {Object.entries(MARKER_GROUPS).map(([groupKey, group]) => (
                <div key={groupKey} className="marker-group">
                  <div className="marker-group-header">
                    {t(group.labelKey)}
                  </div>
                  {group.markers.map((marker) => (
                    <Dropdown.Item
                      key={marker.type}
                      onClick={() => handleMarkerSelect(marker)}
                      className={marker.type === selectedMarkerType ? 'active' : ''}
                    >
                      {renderMarkerButton(marker, marker.type === selectedMarkerType)}
                      <span className="marker-info" title={t(marker.descKey)}>ⓘ</span>
                    </Dropdown.Item>
                  ))}
                </div>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="instructions-section">
          <div className="instruction">
            <span className="instruction-icon add">+</span>
            <span>{t('markerEditing.instructionAdd')}</span>
          </div>
          <div className="instruction">
            <span className="instruction-icon remove">−</span>
            <span>{t('markerEditing.instructionRemove')}</span>
          </div>
        </div>

        <div className="toolbar-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onCancel}
            className="action-btn cancel-btn"
          >
            {t('markerEditing.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSave}
            disabled={!hasPendingChanges}
            className="action-btn save-btn"
          >
            {t('markerEditing.save')}
            {hasPendingChanges && <span className="changes-indicator" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MarkerEditingToolbar;
